import {
  Banner,
  Button,
  Country,
  Flex,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  SearchAsyncSelectField,
  Text,
} from '@electricjs/arc';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { getCountryName } from '@/helpers/getCountryName';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { useLazyGetOrganizationEmployeesQuery } from '@/redux/slices/organizationApiSlice';

type FormData = {
  employee: EmployeeOption | null;
};

type DeployLaptopEmployeeModalProps = {
  /** Country the laptop is stored in, compared against the selected employee's country to warn about a mismatch */
  assetCountry?: string;
  onClose: () => void;
  onSelect: (employeeId: string) => void;
};

/** Asks which employee a warehoused laptop is being deployed to, before sending the user to the storefront */
const DeployLaptopEmployeeModal = ({
  assetCountry,
  onClose,
  onSelect,
}: DeployLaptopEmployeeModalProps) => {
  const { t, i18n } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const [searchForEmployees] = useLazyGetOrganizationEmployeesQuery();

  const { control, handleSubmit, watch } = useForm<FormData>({
    defaultValues: { employee: null },
  });
  const employee = watch('employee');

  const assetCountryCode = assetCountry?.toUpperCase();
  const employeeCountryCode = employee?.address?.country?.toUpperCase();
  const assetCountryName = getCountryName(assetCountryCode as Country);
  const employeeCountryName = getCountryName(employeeCountryCode as Country);
  const hasCountryMismatch =
    !!assetCountryCode &&
    !!employeeCountryCode &&
    assetCountryCode !== employeeCountryCode;

  return (
    <ModalV2
      visible
      hide={onClose}
      ariaLabelledby="deploy-laptop-modal-header-text">
      <ModalHeader showCloseButton width="auto">
        <Text id="deploy-laptop-modal-header-text" variant="heading-2">
          {t('assetDetailsButtonMenu.deployModal.title')}
        </Text>
      </ModalHeader>
      <form
        onSubmit={handleSubmit(({ employee }) => {
          if (employee) onSelect(employee.value);
        })}>
        <ModalBody>
          <Flex flexDirection="column" gap="1.6rem">
            <Text>{t('assetDetailsButtonMenu.deployModal.message')}</Text>
            <SearchAsyncSelectField<EmployeeOption>
              id="deploy-laptop-employee-select"
              name="employee"
              control={control}
              label={t('assetDetailsButtonMenu.deployModal.employeeLabel')}
              placeholder={t(
                'assetDetailsButtonMenu.deployModal.employeePlaceholder'
              )}
              noOptionsText={t(
                'assetDetailsButtonMenu.deployModal.noEmployees'
              )}
              defaultOptions
              cacheOptions
              isSearchable
              customComponents={{
                Option: option => <EmployeeSelectOption data={option.data} />,
              }}
              fetchOptions={async term => {
                const employees = await searchForEmployees({
                  organizationId,
                  ...(term && { term }),
                  offset: 0,
                  limit: 20,
                  ...(!term && { orderBy: 'last_name', sortOrder: 'asc' }),
                }).unwrap();
                return transformEmployeesToOptions(
                  employees,
                  i18n.language,
                  isAdpWfnCanada
                );
              }}
            />
            {hasCountryMismatch && (
              <Banner
                id="deploy-laptop-country-mismatch-banner"
                intent="warning"
                width="100%"
                message={t(
                  'assetDetailsButtonMenu.deployModal.countryMismatchWarning',
                  {
                    assetCountry: assetCountryName,
                    employeeCountry: employeeCountryName,
                  }
                )}
              />
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <ModalFooterActions>
            <Button
              id="deploy-laptop-cancel-button"
              onClick={onClose}
              variant="text">
              {t('assetDetailsButtonMenu.deployModal.cancel')}
            </Button>
            <Button
              id="deploy-laptop-continue-button"
              type="submit"
              variant="fill"
              disabled={!employee}>
              {t('assetDetailsButtonMenu.deployModal.continue')}
            </Button>
          </ModalFooterActions>
        </ModalFooter>
      </form>
    </ModalV2>
  );
};

export default DeployLaptopEmployeeModal;
